import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { ThemeService } from '../../../core/services/common/theme.service';
import { AuthService } from '../../../core/services/user/auth.service';
import { SignInDialogComponent } from './sign-in-dialog.component';
import { UiInteractionService } from '../../../core/services/common/ui-interaction.service';
import { Subscription } from 'rxjs';
import { UserProfileImageComponent } from '../user-profile-image/user-profile-image.component';

@Component({
  selector: 'app-desktop-header',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    SignInDialogComponent,
    UserProfileImageComponent,
  ],
  template: `
    <header
      class="fixed top-0 left-0 right-0 z-50 bg-light-primary dark:bg-dark-primary border-b border-light-border dark:border-dark-border shadow-sm"
    >
      <!-- LAYER 1: Existing header -->
      <div
        class="container mx-auto px-4 py-4 flex justify-between items-center"
      >
        <div class="flex items-center">
          <a
            [routerLink]="['/']"
            class="flex items-center group transition-all duration-200 hover:opacity-80 cursor-pointer"
          >
            <img
              src="favicon.ico"
              alt="Fact Check"
              class="h-8 w-8 mr-2 transition-transform duration-200 ease-in-out group-hover:scale-110"
            />
            <h1
              class="text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary dark:group-hover:text-primary transition-colors duration-200"
            >
              Fact Check Trump
            </h1>
          </a>
        </div>
        <div class="flex items-center space-x-4">
          <!-- Navigation Links -->
          <a
            routerLink="/facts"
            routerLinkActive="text-primary dark:text-primary"
            [routerLinkActiveOptions]="{ exact: false }"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
          >
            Facts
          </a>
          <a
            routerLink="/media"
            routerLinkActive="text-primary dark:text-primary"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
          >
            Media
          </a>
          <a
            routerLink="/search"
            routerLinkActive="text-primary dark:text-primary"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
          >
            Search
          </a>
          <a
            routerLink="/stats"
            routerLinkActive="text-primary dark:text-primary"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
          >
            Stats
          </a>
          <a
            [routerLink]="['/about']"
            routerLinkActive="text-primary dark:text-primary"
            class="text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
          >
            About
          </a>

          <!-- Auth Menu -->
          <ng-container
            *ngIf="auth.currentUser$ | async as user; else loginButton"
          >
            <div class="relative mr-4 group">
              <button class="flex items-center focus:outline-none">
                <app-user-profile-image
                  [displayName]="user.displayName"
                  [email]="user.email"
                  [photoURL]="user.photoURL"
                  [size]="32"
                  class="mr-2"
                >
                </app-user-profile-image>
                <span class="text-sm text-gray-700 dark:text-gray-300 mr-1">{{
                  user.displayName || 'User'
                }}</span>
                <svg
                  class="w-4 h-4 text-gray-700 dark:text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  ></path>
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <div
                class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 invisible group-hover:visible transition-all opacity-0 group-hover:opacity-100 z-50"
              >
                <div class="p-3 border-b border-gray-200 dark:border-gray-700">
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ user.displayName || 'User' }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                    {{ user.email }}
                  </p>
                </div>
                <div class="p-2">
                  <a
                    [routerLink]="['/profile']"
                    class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    Profile
                  </a>
                  <button
                    (click)="signOut()"
                    class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </ng-container>

          <!-- Login Button -->
          <ng-template #loginButton>
            <button
              (click)="openSignInDialog()"
              class="mr-4 text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-200"
            >
              Sign In
            </button>
          </ng-template>

          <!-- Theme Toggle -->
          <button
            (click)="toggleDarkMode()"
            class="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <!-- Sun icon for light mode -->
            <svg
              *ngIf="isDarkMode"
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
              />
            </svg>
            <!-- Moon icon for dark mode -->
            <svg
              *ngIf="!isDarkMode"
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
              />
            </svg>
          </button>
        </div>
      </div>
    </header>

    <!-- Use the Sign-in Dialog Component -->
    <app-sign-in-dialog
      *ngIf="showSignInDialog"
      (closeDialog)="closeSignInDialog()"
    ></app-sign-in-dialog>
  `,
  styles: [],
})
export class DesktopHeaderComponent implements OnInit, OnDestroy {
  isDarkMode = false;
  showSignInDialog = false;
  private uiSubscription: Subscription | null = null;

  constructor(
    private themeService: ThemeService,
    public auth: AuthService,
    private router: Router,
    private uiInteractionService: UiInteractionService,
  ) {}

  ngOnInit(): void {
    this.themeService.darkMode$.subscribe(isDark => {
      this.isDarkMode = isDark;
    });
    this.uiSubscription =
      this.uiInteractionService.requestSignInDialog$.subscribe(() => {
        this.openSignInDialog();
      });
  }

  ngOnDestroy(): void {
    this.uiSubscription?.unsubscribe();
  }

  toggleDarkMode(): void {
    this.themeService.toggleDarkMode();
  }

  openSignInDialog(): void {
    this.showSignInDialog = true;
  }

  closeSignInDialog(): void {
    this.showSignInDialog = false;
  }

  signOut(): void {
    this.auth.signOut();
  }
}
